import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ListMusic, Play, Pause, SkipForward, Square, Trash2 } from "lucide-react";
import { fetchPlaylists, deletePlaylist, resolveImage } from "@/lib/api";

const PlaylistsPanel = ({ songs }) => {
  const [playlists, setPlaylists] = useState([]);
  const [queue, setQueue] = useState(null); // { list, idx, name }
  const [paused, setPaused] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchPlaylists().then(setPlaylists).catch(() => {});
    return () => audioRef.current?.pause();
  }, []);

  const playAt = (list, idx, name) => {
    audioRef.current?.pause();
    const a = new Audio(resolveImage(list[idx].audio_url));
    audioRef.current = a;
    a.onended = () => {
      if (idx + 1 < list.length) playAt(list, idx + 1, name);
      else setQueue(null);
    };
    a.play().catch(() => {});
    setQueue({ list, idx, name });
    setPaused(false);
  };

  const playPlaylist = (pl) => {
    const list = pl.song_ids
      .map((id) => songs.find((s) => s.song_id === id))
      .filter((s) => s && s.audio_url);
    if (list.length) playAt(list, 0, pl.name);
  };

  const stop = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setQueue(null);
    setPaused(false);
  };

  const togglePause = () => {
    if (!audioRef.current) return;
    if (paused) {
      audioRef.current.play().catch(() => {});
      setPaused(false);
    } else {
      audioRef.current.pause();
      setPaused(true);
    }
  };

  const next = () => {
    if (!queue) return;
    if (queue.idx + 1 < queue.list.length) playAt(queue.list, queue.idx + 1, queue.name);
    else stop();
  };

  const remove = async (id) => {
    await deletePlaylist(id).catch(() => {});
    setPlaylists((ps) => ps.filter((p) => p.playlist_id !== id));
    if (queue?.name && playlists.find((p) => p.playlist_id === id)?.name === queue.name) stop();
  };

  const current = queue?.list[queue.idx];

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.16 }}
      className="mt-6 rounded-3xl border border-[#E2DDD0] bg-white p-8"
      data-testid="playlists-panel"
    >
      <h2 className="flex items-center gap-2 font-display text-xl font-bold text-slate-900">
        <ListMusic className="h-5 w-5 text-[#0284C7]" /> My playlists
      </h2>
      {playlists.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">
          No playlists yet — tap the playlist icon on any song to start one.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {playlists.map((pl) => (
            <li key={pl.playlist_id} className="flex items-center gap-3 rounded-2xl bg-[#F8F5EC] p-4" data-testid={`playlist-row-${pl.playlist_id}`}>
              <button
                onClick={() => playPlaylist(pl)}
                data-testid={`playlist-play-${pl.playlist_id}`}
                aria-label={`Play ${pl.name}`}
                className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#0284C7] text-white transition-transform hover:scale-110 active:scale-95"
              >
                <Play className="ml-0.5 h-5 w-5 fill-white" />
              </button>
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-base font-semibold text-slate-900">{pl.name}</p>
                <p className="text-xs font-bold text-slate-400">{pl.song_ids.length} songs</p>
              </div>
              <button
                onClick={() => remove(pl.playlist_id)}
                data-testid={`playlist-delete-${pl.playlist_id}`}
                aria-label={`Delete ${pl.name}`}
                className="grid h-9 w-9 place-items-center rounded-full text-slate-300 transition-colors hover:bg-[#FFE5E5] hover:text-[#DC2626]"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <AnimatePresence>
        {queue && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-full border border-white/10 bg-slate-900/95 py-2.5 pl-4 pr-3 text-white shadow-2xl backdrop-blur"
            data-testid="playlist-player"
          >
            <span className="grid h-9 w-9 place-items-center rounded-full bg-[#0284C7]">
              <ListMusic className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="max-w-44 truncate font-display text-sm font-semibold" data-testid="playlist-now-title">
                {current?.title}
              </p>
              <p className="text-xs text-slate-400">
                {queue.name} — {queue.idx + 1} of {queue.list.length}
              </p>
            </div>
            <button
              onClick={togglePause}
              data-testid="playlist-pause-btn"
              aria-label={paused ? "Resume playlist" : "Pause playlist"}
              className="grid h-10 w-10 place-items-center rounded-full bg-white/10 transition-colors hover:bg-white/20"
            >
              {paused ? <Play className="ml-0.5 h-4 w-4 fill-white" /> : <Pause className="h-4 w-4 fill-white" />}
            </button>
            <button
              onClick={next}
              data-testid="playlist-next-btn"
              aria-label="Next song"
              className="grid h-10 w-10 place-items-center rounded-full bg-white/10 transition-colors hover:bg-white/20"
            >
              <SkipForward className="h-4 w-4" />
            </button>
            <button
              onClick={stop}
              data-testid="playlist-stop-btn"
              aria-label="Stop playlist"
              className="grid h-10 w-10 place-items-center rounded-full bg-white/10 transition-colors hover:bg-[#FF6B6B]"
            >
              <Square className="h-3.5 w-3.5 fill-current" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PlaylistsPanel;
